import React, { ReactElement } from 'react';
import {
  Component,
  Position,
  PuzzleToggle
} from "puzzle";
import {
  extendDataItem,
  mapTree,
  TreeList,
  TreeListCellProps,
  TreeListColumnProps,
  TreeListExpandChangeEvent,
  TreeListRowClickEvent,
  TreeListRowProps
} from "@progress/kendo-react-treelist";
import { BATCH_LANGUAGE, JOB_PARENT, RESPONSE_DATA_WORD_COUNT, STATISTICS, WORD_COUNT_STATS } from "./Types";
import { Keys } from "@progress/kendo-react-common";
import _ from 'lodash';
import { removeUnnecessaryAttributes, setTableCaption } from "../../util/Util";
import classNames from "classnames";

export type PROPS = {
  wordCountData: RESPONSE_DATA_WORD_COUNT,
  submissionId: string | number,
  submission?: any,
  preExpandJob?: BATCH_LANGUAGE,
  groupBy: 'batches' | 'languages'
};

export type STATE = {
  activeTabLanguages: boolean,
  data: any[]
  expandedIds: (number | string)[],
  activeTab: string
}

class WordCount extends Component<PROPS, STATE> {
  treeListRef: any = React.createRef();
  public state: STATE = {
    activeTabLanguages: true,
    data: [],
    expandedIds: [],
    activeTab: 'language'
  };

  static defaultProps = {
    wordCountData: {},
    submissionId: '',
    submission: undefined,
    preExpandJob: undefined
  };

  stateSnap: STATE | undefined;
  jobId: number | undefined;
  parentId: string | undefined;

  columns: TreeListColumnProps[] = [];

  jobFilesPerPage: number = 10000;

  defaultColumns: TreeListColumnProps[] = [
    {
      field: 'name',
      expandable: true,
      title: this.state.activeTabLanguages ? this.translate('target_language') : this.translate('batch'),
    }
  ];

  /**
   * Initially creates mapped data, and selected arrays for all submissions
   * Selected array stores into state, since only that can be changed
   */
  moduleDidMount = () => {
    this.loadData(this.props.wordCountData);
    setTableCaption(this.treeListRef.element, this.translate('SRL-table-caption'));
  };

  /**
   * @inheritDoc
   */
  componentDidUpdate = (prevProps: PROPS, prevState: STATE) => {
    if (!(_.isEqual(this.props, prevProps) && _.isEqual(this.state, prevState))) {
      removeUnnecessaryAttributes(this.treeListRef.element);
    }
  };

  /**
   * Returns basic column object
   */
  createColumnFromStatistic = ({ name }: WORD_COUNT_STATS): TreeListColumnProps => {
    const shouldBeTranslated = !name.includes('%');
    return {
      field: name,
      title: shouldBeTranslated ? this.translate(name) : name
    }
  };

  /**
   *  Maps all properties (jobNames) of jobs object
   */
  createJobDataItems = (jobs: any, parentId: string) => {
    const jobNames = Object.keys(jobs).sort();
    return jobNames.map(this.mapJobDataItem.bind(this, jobs, parentId));
  };


  /**
   *  Finds statistics object and merges it to one object together with totalWordCount and type of statistics
   */
  createStatisticsObject = (stats: STATISTICS) => {
    const {
      totalWordCount,
      cumulativeTmStatistics: cumulative,
      primaryTmStatistics: primary,
      canceledTmStatistics: canceled
    } = stats;
    let statsType = '';
    if (cumulative) {
      statsType = 'cumulative';
      if (canceled) this.subtractCanceledStats(cumulative, canceled);

    } else if (primary) {
      statsType = 'primary';
      if (canceled) this.subtractCanceledStats(primary, canceled);

    } else if (canceled) {
      statsType = 'canceled';
    }
    let statsObject = null;
    if (statsType) {
      statsObject = (stats as any)[statsType + 'TmStatistics'].reduce(this.mergeStatistics, {});
      Object.assign(statsObject, { statsType }, { totalWordCount: totalWordCount === null ? this.translate('notAssigned') : totalWordCount });
    }
    return statsObject;
  };

  /**
   * Removes matching id from array being filtered
   */
  filterItemsNotMatchingId = (targetId: number | string, id: number | string) => targetId !== id;

  /**
   * Initially creates columns, sets activeTabLanguages, expandedIdsarray and creates data for TreeList
   */
  loadData = (responseObj: RESPONSE_DATA_WORD_COUNT) => {
    const { preExpandJob } = this.props;
    if (responseObj.success) {
      const response = _.cloneDeep(responseObj);
      const activeTabLanguages = !!response.languages;
      const activeTab = activeTabLanguages ? 'language' : 'batch';
      const submissionData = response[activeTabLanguages ? 'languages' : 'batches'];
      if (submissionData) {
        const { statistics } = submissionData[0];
        let expandedIds: any[] = [];
        this.updateColumns(statistics);
        let data = submissionData.map(this.mapJobParents.bind(this, activeTabLanguages));
        data = _.sortBy(data, 'name')
        if (preExpandJob) {
          this.setState({ data, activeTabLanguages }, () => {
            this.preExpandJob(preExpandJob, data, activeTabLanguages, activeTab);
          });
        } else if (!activeTabLanguages) {
          expandedIds = data.map((item: any) => item.id);
          this.setState({ data, activeTabLanguages, expandedIds });
        } else {
          this.setState({ data, activeTabLanguages, expandedIds });
        }
      }
    }
  };

  /**
   * Initially if there is a job to be pre-expanded
   * Finds the target job in data, expands it and get files for it
   */
  preExpandJob = (job: any, data: any[], activeTabLanguages: boolean, activeTab: string) => {
    let parentId: string, matchProp: string, matchValue: string;
    let jobId: number | undefined = undefined;
    let expandedIds: (string | number)[] = [];
    let { batchName, targetLanguage } = job;
    if (activeTabLanguages) {
      if (!targetLanguage) {
        targetLanguage = data.find((item) => item.batchName === batchName)?.targetLanguageLocale;
        if (!targetLanguage) {
          targetLanguage = data.find((item) => _.find(item.subItems, { 'name': item.batchName })).targetLanguageLocale;
        }
      }
      parentId = targetLanguage;
      matchProp = 'batchName';
      matchValue = batchName;
    } else {
      parentId = batchName;
      matchProp = 'targetLanguageLocale';
      matchValue = targetLanguage;
    }
    let parentSubitems = _.find(data, (item) => item.id === parentId).subItems;

    expandedIds.push(parentId);
    if (targetLanguage) {
      jobId = _.find(parentSubitems, (item) => item[matchProp] === matchValue).id;
      let targetLanguageParam = job.targetLanguage || targetLanguage;
      this.getJobFiles(0, job.batchName, targetLanguageParam);
    } else {
      this.setState({ expandedIds });
    }

    this.stateSnap = { data, activeTabLanguages, expandedIds, activeTab };
    this.jobId = jobId;
    this.parentId = parentId;
  };

  /**
   * Gets job files from server and calls loadJobWithFiles on success
   */
  getJobFiles = (pageIndex: number, batchName: string, targetLanguage: string) => {
    const { submissionId } = this.props;
    const wordCountParams = {
      index: pageIndex,
      pageSize: this.jobFilesPerPage,
      batchName,
      targetLanguage
    };
    this.dispatch('wordCountGetJobFiles', Position.end, {
      submissionId,
      wordCountParams
    });
  };


  /**
   * Fills job with jobFiles loaded from server
   */
  loadJobWithFiles = (response: RESPONSE_DATA_WORD_COUNT) => {
    if (response.success) {
      const jobId = this.jobId;
      const parentId = this.parentId;
      const state = this.stateSnap || this.state;
      const expandedIds = state.expandedIds;
      const data = state.data;
      const { jobDocuments } = response;
      //@ts-ignore
      const documentNames = Object.keys(jobDocuments);
      if (jobId !== undefined && parentId !== undefined) {
        expandedIds.push(jobId);
        const jobParent = _.find(data, (item) => item.id === parentId);
        const job = _.find(jobParent.subItems, (item) => item.id === jobId);
        const documents = documentNames.map(this.mapJobFile.bind(this, jobId, jobDocuments));
        job.subItems = [...documents];
        //TODO PHASE 2: there should be increased job.nextPageIndex if next page exists, to load next page with another click
      }
      this.setState({ ...state, expandedIds, data });
      this.stateSnap = undefined;
      this.jobId = undefined;
      this.parentId = undefined;
    }
  };

  /**
   *  Maps jobs for data tree
   *  When grouping is by batch -> job has targetLanguageLocale property and batchName is set to parentId
   *  otherwise -> job doesn't have targetLanguageLocale property, so batchName is also jobName, and targetLanguageLocale is parentId
   *  parentId: batchName | targetLanguageLocale
   *  jobName: targetLanguageDisplayValue | batchName
   */
  mapJobDataItem = (jobs: any, parentId: string, jobName: string) => {
    const job = jobs[jobName];
    const { targetLanguageLocale, jobId } = job;
    let jobObject = {
      name: jobName,
      subItems: [],
      id: jobId,
      parentId,
      type: 'job',
      nextPageIndex: 0,
      batchName: targetLanguageLocale ? parentId : jobName,
      targetLanguageLocale: targetLanguageLocale || parentId
    };
    Object.assign(jobObject, this.createStatisticsObject(job));
    return jobObject
  };

  /**
   * Maps files for data tree
   */
  mapJobFile = (jobId: number, documents: any, fileName: string) => {
    const file = documents[fileName];
    const { find } = _;
    const job = this.state.data.reduce((acc: any, curr: any) => {
      if (!acc) {
        const match = find(curr.subItems, { id: jobId });
        if (match) {
          acc = match;
        }
      }
      return acc;
    }, null);
    const fileObject = {
      batchName: job.batchName,
      targetLanguageLocale: job.targetLanguageLocale,
      name: fileName,
      subItems: [],
      id: jobId + '-' + file.documentId,
      targetId: file.documentId,
      parentId: jobId,
      type: 'file'
    };
    Object.assign(fileObject, this.createStatisticsObject(file));
    return fileObject;
  };

  /**
   * Maps job parents for data tree
   */
  mapJobParents = (activeTabLanguages: boolean, jobParent: JOB_PARENT) => {
    let name, id;
    if (activeTabLanguages) {
      id = jobParent.targetLanguageLocale;
      name = jobParent.targetLanguageDisplayName;
    } else {
      name = id = jobParent.batchName;
    }
    let batchName = jobParent.batchName;
    if (!batchName) {
      batchName = Object.keys(jobParent.data)[0];
    }
    const parentObject = {
      batchName: batchName,
      targetLanguageLocale: jobParent.targetLanguageLocale,
      name,
      subItems: this.createJobDataItems(jobParent.data, id),
      id,
      parentId: null,
      type: 'jobParent'
    };
    Object.assign(parentObject, this.createStatisticsObject(jobParent.statistics));
    return parentObject;
  };

  /**
   *  Reducer to merge all statistics to one object
   */
  mergeStatistics = (allStats: any, stat: WORD_COUNT_STATS) => {
    return {
      ...allStats,
      [stat.name]: stat.wordCount === null ? this.translate("notAssigned") : stat.wordCount
    }
  };

  /**
   * Tree List cell renderer
   * @param defaultCell
   * @param props
   */
  onCellRender = (defaultCell: ReactElement<HTMLTableCellElement> | null, props: TreeListCellProps): ReactElement<HTMLTableCellElement> => {
    const { dataItem, field } = props;
    const { expandedIds, activeTabLanguages } = this.state;
    const { type, id, batchName, targetLanguageLocale } = dataItem;
    let cell = defaultCell || <td children={[]} />;
    const treeLevel = `${this.translate('SRL-level')} ${props.level.length}`;
    const children = [...cell.props.children];
    switch (field) {
      case 'name':
        const dataQA = `${batchName ? `Batch: ${batchName};` : ''}${targetLanguageLocale ? `Target Language: ${targetLanguageLocale}` : ''}`;
        let ariaLabel = activeTabLanguages ? this.translate('languages') + ' ' + dataItem.name : this.translate('batchName') + ' ' + dataItem.name;
        if (type === 'job' && !expandedIds.includes(id)) {
          children[0] = (<span
            aria-hidden={'true'}
            className="k-icon k-i-expand"
            onClick={this.onRowClick.bind(this, { dataItem })}
            key={'expand-' + dataItem.id}
          />);
          ariaLabel = !activeTabLanguages ? this.translate('languages') + ' ' + dataItem.name : this.translate('batchName') + ' ' + dataItem.name;
          cell = (<cell.type {...cell.props} aria-label={`${treeLevel} ${ariaLabel}`}
                             data-qa={dataQA}>{children}</cell.type>);
        } else {
          ariaLabel = type === 'file' ? this.translate('fileName') + ' ' + dataItem.name : ariaLabel;
          if (!!children[0][0]) {
            let iconElement = children[0][0];
            if (iconElement.type === 'span') {
              children[0][0] = {
                ...iconElement,
                props: {
                  ...iconElement.props,
                  'aria-hidden': 'true'
                }
              }
            }
          }
          cell = (<cell.type
            {...cell.props}
            aria-label={`${treeLevel} ${ariaLabel}`}
            data-qa={dataQA}
          >
            {children[0]}
            {dataItem.statsType === 'canceled' ? <span className={'tp-cancelled-tag'}>{this.translate('cancelTag')}</span> : null}
            {children[1]}
          </cell.type>);
        }
        break;
      default:
        cell = (
          <cell.type
            {...cell.props}
            aria-label={this.translate(field || '') + ' ' + children[1]}
          >
            <span>{children}</span>
          </cell.type>
        );
        break;
    }
    return cell;
  };

  /**
   * Removes or adds id of clicked item to expandedIds array
   * @param e
   */
  onExpandChange = (e: TreeListExpandChangeEvent) => {
    const { id } = e.dataItem;
    this.setState({
      expandedIds: e.value ?
        this.state.expandedIds.filter(this.filterItemsNotMatchingId.bind(this, id)) :
        [...this.state.expandedIds, id]
    });
  };

  /**
   * Row Click Event handler
   * Expands current row
   * If job is clicked, then dispatch is called to get jobFiles from server
   */
  onRowClick = (ev: TreeListRowClickEvent | { dataItem: any }) => {
    const expandedIds = [...this.state.expandedIds];
    const { id, type, parentId, nextPageIndex: index, batchName, targetLanguageLocale: targetLanguage } = ev.dataItem;
    const indexInExpandedIdsArray = expandedIds.indexOf(id);
    if (indexInExpandedIdsArray === -1) {
      if (type === 'job') {
        this.jobId = id;
        this.parentId = parentId;
        this.getJobFiles(index, batchName, targetLanguage);
      } else {
        expandedIds.push(id);
        this.setState({ expandedIds });
      }
    } else {
      expandedIds.splice(indexInExpandedIdsArray, 1);
      this.setState({ expandedIds });
    }
  };

  /**
   * Set currently active tab e.g. batches/languages
   */
  setActiveTab = ((tab: string) => {
    this.setState({ activeTab: tab }, () => {
      const { activeTab } = this.state;
      const action = activeTab !== 'language' ? 'getWordCountDataBatchGrouping' : 'getWordCountDataLanguageGrouping';
      this.dispatch(action, Position.end);
    });
  });

  /**
   * On enter and space
   * @param dataItem
   * @param e
   */
  rowKeyUpHandler = (dataItem: any, e: KeyboardEvent) => {
    let { keyCode } = e;
    if ([Keys.enter, Keys.space].includes(keyCode)) {
      this.onRowClick({ dataItem: dataItem });
    }
  };

  /**
   * Custom rendering for rows
   * @param row
   * @param props
   */
  onRowRender = (row: ReactElement<HTMLTableRowElement>, props: TreeListRowProps): ReactElement<HTMLTableCellElement> => {
    let { dataItem } = props;
    const { activeTabLanguages } = this.state;
    if (!activeTabLanguages && !dataItem.parentId) {
      const skipKeys = ["name", "subItems", "id", "parentId", "type", "statsType", "expandedIds"]
      for (let key in dataItem) {
        if (dataItem.hasOwnProperty(key) && !_.includes(skipKeys, key)) {
          dataItem[key] = null;
        }
      }
    }
    let isExpanded = this.state.expandedIds.includes(dataItem.id);
    const className = classNames(
      `${row.props.className} tp-row-stats-${dataItem.statsType} tp-${dataItem.type}-row`,
      {
        'tp-row-expanded': isExpanded,
      }
    );
    return (
      <row.type
        {...row.props}
        aria-expanded={isExpanded ? 'true' : 'false'}
        className={className}
        onKeyUp={this.rowKeyUpHandler.bind(this, dataItem)}
        tabIndex={0}
      >
        {row.props.children}
      </row.type>
    );
  };

  /**
   * Initially adds columns from statistics to default columns
   * @param statistics
   */
  updateColumns = (statistics: STATISTICS) => {
    const stats = statistics.cumulativeTmStatistics || statistics.canceledTmStatistics || statistics.primaryTmStatistics;
    if (stats) {
      this.columns = [...this.defaultColumns,
        ...stats.map(this.createColumnFromStatistic),
        {
          field: 'totalWordCount',
          title: this.translate('totalWordCount')
        }];
    }
  };

  /**
   * Expands items on render
   * @param data
   */
  updateFields = (data: any[]): any[] => {
    const { expandedIds } = this.state;
    return mapTree(data, 'subItems', (item: any) =>
      extendDataItem(item, 'subItems', {
        expandedIds: expandedIds.includes(item.id),
      })
    );
  };

  /**
   * In case there are both cumulative (or primary) and canceled statistics, totalStats should be reduced by canceledStats
   * This method reduces it
   * note: Both total and canceled stats are Arrays with data in the same order
   */
  subtractCanceledStats = (totalStats: WORD_COUNT_STATS[], canceledStats: WORD_COUNT_STATS[]) => {
    let i = totalStats.length;
    let currentTotal, canceledWordCount;
    while (i--) {
      currentTotal = totalStats[i];
      canceledWordCount = canceledStats[i] && canceledStats[i].wordCount;
      if (currentTotal && currentTotal.wordCount && canceledWordCount) {
        //@ts-ignore
        currentTotal.wordCount -= canceledWordCount
      }
    }
  };

  renderBody(): any {
    const { data, activeTab } = this.state;
    return (
      <>
        {!!data &&
          <>
            <PuzzleToggle
              id={this.moduleName}
              switcherLabel={this.translate('SRL-switcherLabel')}
              activeTab={activeTab}
              setActiveTab={this.setActiveTab}
              checked={activeTab === 'batch'}
              switcherOptions={[{
                name: 'language',
                locale: this.translate('SRL-firstOptionLabel')
              }, {
                name: 'batch',
                locale: this.translate('SRL-secondOptionLabel')
              }]}
            />
            <TreeList
              ref={(treeList: any) => this.treeListRef = treeList}
              tableProps={{ tabIndex: 0 }}
              rowHeight={40}
              cellRender={this.onCellRender}
              className={'tp-word-count-list tp-no-border'}
              rowRender={this.onRowRender}
              data={this.updateFields(data)}
              expandField={'expandedIds'}
              subItemsField={'subItems'}
              onExpandChange={this.onExpandChange}
              columns={this.columns}
              onRowClick={this.onRowClick}
            />
          </>
        }
      </>
    )
  }
}

export default WordCount;

